import { useNavigate } from 'react-router-dom';
import {
    Package,
    CheckCircle,
    XCircle,
    ClipboardList,
    Bell,
    CheckCheck,
    FileText,
    UserPlus,
    UserCheck,
    UserX,
    UserMinus,
    Wrench,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNotificationOperations, NotificationLog } from '@/hooks/useNotificationOperations';
import { cn } from '@/lib/utils';
import { NotificationType } from '@mesterplan/shared-types';
import { CREDITOR_INVOICE_NOTIFICATION_TYPES } from '@mesterplan/api-client';

interface NotificationPanelProps {
    onClose?: () => void;
}

const NOTIFICATION_ICONS: Partial<Record<NotificationType, React.ElementType>> = {
    material_added: Package,
    material_approved: CheckCircle,
    material_rejected: XCircle,
    task_assigned: ClipboardList,
    task_updated: ClipboardList,
    task_reminder: Bell,
    creditor_invoice_received: FileText,
    creditor_invoice_unmatched: FileText,
    partner_invitation_received: UserPlus,
    partner_invitation_accepted: UserCheck,
    partner_invitation_declined: UserX,
    partner_access_revoked: UserMinus,
    service_due: Wrench,
};

const NOTIFICATION_COLORS: Partial<Record<NotificationType, string>> = {
    material_added: 'text-amber-500',
    material_approved: 'text-green-500',
    material_rejected: 'text-red-500',
    task_assigned: 'text-blue-500',
    task_updated: 'text-blue-500',
    task_reminder: 'text-purple-500',
    creditor_invoice_received: 'text-green-500',
    creditor_invoice_unmatched: 'text-yellow-500',
    partner_invitation_received: 'text-teal-500',
    partner_invitation_accepted: 'text-green-500',
    partner_invitation_declined: 'text-red-500',
    partner_access_revoked: 'text-red-500',
    service_due: 'text-amber-500',
};

function formatRelativeTime(sentAt: string): string {
    const date = new Date(sentAt);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMinutes = Math.floor(diffMs / (1000 * 60));
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffMinutes < 1) return 'Lige nu';
    if (diffMinutes < 60) return `${diffMinutes} min siden`;
    if (diffHours < 24) return `${diffHours} timer siden`;
    if (diffDays < 7) return `${diffDays} dage siden`;

    return date.toLocaleDateString('da-DK', {
        day: 'numeric',
        month: 'short',
    });
}

export function NotificationPanel({ onClose }: NotificationPanelProps) {
    const navigate = useNavigate();
    const {
        useFetchUserNotifications,
        useMarkNotificationRead,
        useMarkAllNotificationsRead,
    } = useNotificationOperations();

    const { data: notifications, isLoading } = useFetchUserNotifications(20);
    const markRead = useMarkNotificationRead();
    const markAllRead = useMarkAllNotificationsRead();

    const unreadNotifications = notifications?.filter(n => !n.readAt) || [];
    const unreadCount = unreadNotifications.length;

    const handleNotificationClick = (notification: NotificationLog) => {
        // Mark as read
        if (!notification.readAt) {
            markRead.mutate(notification.id);
        }

        // Navigate based on notification type
        if (CREDITOR_INVOICE_NOTIFICATION_TYPES.includes(notification.type)) {
            const creditorInvoiceId = notification.data?.creditorInvoiceId as string | undefined;
            if (creditorInvoiceId) {
                navigate(`/efaktura?invoiceId=${creditorInvoiceId}`);
            } else {
                navigate('/efaktura');
            }
            onClose?.();
            return;
        }

        // Partner invitation notifications
        if (notification.type === 'partner_invitation_received') {
            const inviteToken = notification.data?.inviteToken as string | undefined;
            const partnerEmail = notification.data?.partnerEmail as string | undefined;
            if (inviteToken && partnerEmail) {
                navigate(`/invitations/accept?token=${inviteToken}&email=${encodeURIComponent(partnerEmail)}`);
            }
            onClose?.();
            return;
        }

        if (notification.type === 'partner_invitation_accepted' ||
            notification.type === 'partner_invitation_declined' ||
            notification.type === 'partner_access_revoked') {
            const entityType = notification.data?.entityType as string | undefined;
            const entityId = notification.data?.entityId as string | undefined;
            if (entityId) {
                if (entityType === 'quote') {
                    navigate(`/quotes/${entityId}`);
                } else {
                    navigate(`/projects/${entityId}`);
                }
            }
            onClose?.();
            return;
        }

        const projectId = notification.data?.projectId as string | undefined;
        if (projectId) {
            navigate(`/projects/${projectId}`);
            onClose?.();
            return;
        }

        // A service visit with no project yet sits in the planner's "Ikke tildelt" on its day.
        if (notification.type === 'service_due' && notification.data?.route === 'planning') {
            const date = notification.data?.date as string | undefined;
            navigate(date ? `/planning?date=${encodeURIComponent(date)}` : '/planning');
            onClose?.();
        }
    };

    const handleMarkAllRead = () => {
        const unreadIds = unreadNotifications.map(n => n.id);
        markAllRead.mutate(unreadIds);
    };

    return (
        <div className="w-96 max-h-[70vh] flex flex-col bg-white dark:bg-dark-card rounded-lg shadow-xl border border-bordergray dark:border-dark-border overflow-hidden">
            {/* Header */}
            <div className="px-4 py-3 border-b border-bordergray dark:border-dark-border flex items-center justify-between">
                <h3 className="text-[15px] font-semibold text-dark dark:text-dark-text">Notifikationer</h3>
                {unreadCount > 0 && (
                    <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleMarkAllRead}
                        disabled={markAllRead.isPending}
                        className="h-7 text-[12px] text-muted dark:text-dark-muted hover:text-dark dark:hover:text-dark-text"
                    >
                        <CheckCheck className="h-3.5 w-3.5 mr-1" />
                        Marker alle som læst
                    </Button>
                )}
            </div>

            {/* Notification list */}
            <div className="flex-1 overflow-y-auto">
                {isLoading ? (
                    <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary" />
                    </div>
                ) : notifications && notifications.length > 0 ? (
                    <div className="divide-y divide-bordergray dark:divide-dark-border">
                        {notifications.map((notification) => (
                            <NotificationItem
                                key={notification.id}
                                notification={notification}
                                onClick={() => handleNotificationClick(notification)}
                            />
                        ))}
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-muted dark:text-dark-muted">
                        <Bell className="h-10 w-10 opacity-30 mb-3" />
                        <p className="text-[14px]">Ingen notifikationer</p>
                    </div>
                )}
            </div>
        </div>
    );
}

interface NotificationItemProps {
    notification: NotificationLog;
    onClick: () => void;
}

function NotificationItem({ notification, onClick }: NotificationItemProps) {
    const Icon = NOTIFICATION_ICONS[notification.type] || Bell;
    const iconColor = NOTIFICATION_COLORS[notification.type] || 'text-muted';
    const isUnread = !notification.readAt;

    return (
        <button
            type="button"
            onClick={onClick}
            className={cn(
                'w-full px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-dark-hover transition-colors flex gap-3',
                isUnread && 'bg-primary-50/30 dark:bg-primary-900/20'
            )}
        >
            <div className={cn('mt-0.5', iconColor)}>
                <Icon className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                    <p className={cn(
                        'text-[13px] text-dark dark:text-dark-text',
                        isUnread && 'font-medium'
                    )}>
                        {notification.title}
                    </p>
                    {isUnread && (
                        <span className="w-2 h-2 rounded-full bg-primary shrink-0 mt-1.5" />
                    )}
                </div>
                <p className="text-[12px] text-muted dark:text-dark-muted mt-0.5 line-clamp-2">
                    {notification.body}
                </p>
                <p className="text-[11px] text-text-tertiary dark:text-dark-muted/70 mt-1">
                    {formatRelativeTime(notification.sentAt)}
                </p>
            </div>
        </button>
    );
}

export default NotificationPanel;
